import { ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getGameFromTitle, removeGameCode } from "@/lib/gameCategories";

interface MatchCardProps {
  id: string;
  status: "live" | "ended" | "upcoming";
  category: string;
  time: string;
  question: string;
  teamA: {
    name: string;
    score?: number;
    odds?: string;
  };
  teamB: {
    name: string;
    score?: number;
    odds?: string;
  };
  bestOf?: string;
}

export function MatchCard({ id, status, category, time, question, teamA, teamB, bestOf }: MatchCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  
  // Extract game info from title
  const gameInfo = getGameFromTitle(question);
  const cleanQuestion = removeGameCode(question);

  const handleCardClick = () => {
    navigate(`/launchpad/${id}`);
  };

  return (
    <Card 
      className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer overflow-hidden"
      onClick={handleCardClick}
    >
      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="p-3 space-y-3">
          {/* Top Row: Status + Game Code */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {status === "live" && (
                <Badge className="bg-success/20 text-success border-success/50 animate-pulse text-xs">
                  Live
                </Badge>
              )}
              {status === "ended" && (
                <Badge variant="outline" className="text-muted-foreground text-xs">
                  Ended
                </Badge>
              )}
              {status === "upcoming" && (
                <Badge variant="outline" className="text-muted-foreground text-xs">
                  Soon
                </Badge>
              )}
              {gameInfo && (
                <Badge variant="default" className="font-mono text-[10px]">
                  {gameInfo.code}
                </Badge>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className={cn(
                "text-muted-foreground hover:text-foreground transition-all",
                isExpanded && "rotate-180"
              )}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>

          {/* Question */}
          <div>
            {gameInfo && (
              <p className="text-xs text-muted-foreground mb-1">{gameInfo.name}</p>
            )}
            <h3 className="text-sm font-medium text-foreground line-clamp-2">
              {cleanQuestion}
            </h3>
          </div>

          {/* YES vs NO */}
          <div className="flex items-center justify-between gap-2">
            {/* YES */}
            <div className="flex-1 text-center py-2 px-3 bg-background/50 rounded">
              <div className="text-sm font-semibold text-foreground">YES</div>
              {teamA.odds && (
                <div className="text-xs text-muted-foreground mt-0.5">{teamA.odds}</div>
              )}
            </div>

            {/* Score/Dash */}
            <div className="flex items-center gap-1 shrink-0">
              {status === "ended" || status === "live" ? (
                <>
                  <div className="bg-background px-2 py-1 rounded text-center min-w-[28px]">
                    <span className="text-sm font-bold text-foreground">
                      {teamA.score ?? 0}
                    </span>
                  </div>
                  <span className="text-muted-foreground text-xs">-</span>
                  <div className="bg-background px-2 py-1 rounded text-center min-w-[28px]">
                    <span className="text-sm font-bold text-foreground">
                      {teamB.score ?? 0}
                    </span>
                  </div>
                </>
              ) : (
                <span className="text-muted-foreground text-sm px-2">-</span>
              )}
            </div>

            {/* NO */}
            <div className="flex-1 text-center py-2 px-3 bg-background/50 rounded">
              <div className="text-sm font-semibold text-foreground">NO</div>
              {teamB.odds && (
                <div className="text-xs text-muted-foreground mt-0.5">{teamB.odds}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex items-center gap-4 p-4">
        {/* Status Badge */}
        <div className="flex flex-col items-center gap-2 shrink-0 w-24">
          {status === "live" && (
            <Badge className="bg-success/20 text-success border-success/50 animate-pulse">
              Live
            </Badge>
          )}
          {status === "ended" && (
            <Badge variant="outline" className="text-muted-foreground">
              Ended
            </Badge>
          )}
          {status === "upcoming" && (
            <Badge variant="outline" className="text-muted-foreground">
              Soon
            </Badge>
          )}
          {gameInfo && (
            <Badge variant="default" className="font-mono text-xs">
              {gameInfo.code}
            </Badge>
          )}
        </div>
        
        {/* Question/Market Title */}
        <div className="flex-1 min-w-0 px-4">
          {gameInfo && (
            <p className="text-xs text-muted-foreground mb-1">{gameInfo.name}</p>
          )}
          <h3 className="text-base font-medium text-foreground line-clamp-2">
            {cleanQuestion}
          </h3>
        </div>

        <div className="flex-1 flex justify-center">
          {/* Match Details */}
          <div className="flex items-center gap-3 shrink-0">
          {/* Team A (YES) */}
          <div className="text-right min-w-[80px]">
            <h3 className="text-base font-semibold text-foreground">
              {teamA.name}
            </h3>
            {teamA.odds && (
              <p className="text-xs text-muted-foreground">
                {teamA.odds}
              </p>
            )}
          </div>

          {/* Score or Dash */}
          <div className="flex items-center gap-2">
            {status === "ended" || status === "live" ? (
              <>
                <div className="bg-background px-3 py-1.5 rounded text-center min-w-[36px]">
                  <span className="text-lg font-bold text-foreground">
                    {teamA.score ?? 0}
                  </span>
                </div>
                <span className="text-muted-foreground text-sm">-</span>
                <div className="bg-background px-3 py-1.5 rounded text-center min-w-[36px]">
                  <span className="text-lg font-bold text-foreground">
                    {teamB.score ?? 0}
                  </span>
                </div>
              </>
            ) : (
              <span className="text-muted-foreground text-sm px-2">-</span>
            )}
          </div>

          {/* Team B (NO) */}
          <div className="text-left min-w-[80px]">
            <h3 className="text-base font-semibold text-foreground">
              {teamB.name}
            </h3>
            {teamB.odds && (
              <p className="text-xs text-muted-foreground">
                {teamB.odds}
              </p>
            )}
          </div>
        </div>
        </div>

        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className={cn(
            "text-muted-foreground hover:text-foreground transition-all shrink-0",
            isExpanded && "rotate-180"
          )}
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-background/50">
          <div className="text-sm text-muted-foreground">
            {status === "upcoming" && (
              <span>Starts: {time}</span>
            )}
            {status === "live" && (
              <span>Live now</span>
            )}
            {status === "ended" && (
              <span className="text-success">Market Resolved</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

